import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, TextField, InputAdornment, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Pagination, CircularProgress, Select, MenuItem, FormControl, InputLabel, Stack } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../../services/api';
import { Link } from 'react-router-dom';

const AdminRecruitmentList = () => {
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);

  const load = async (params={}) => {
    try {
      setLoading(true);
      const p = params.page || page;
      const s = params.size || size;
      const q = params.search !== undefined ? params.search : search;
      const res = await api.get('/recruitment/applications', { params: { page: p, size: s, search: q } });
      setRows(res.data.data || []);
      setTotal(res.data.total || 0);
      setPage(res.data.page || p);
      setSize(res.data.size || s);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus data recruitment ini?')) {
      try {
        await api.delete(`/recruitment/applications/${id}`);
        load({ page: 1 });
      } catch (e) {
        console.error(e);
        alert('Gagal menghapus data recruitment');
      }
    }
  };

  const handleSizeChange = (event) => {
    const newSize = parseInt(event.target.value);
    setSize(newSize);
    load({ page: 1, size: newSize });
  };

  // Initial fetch on mount; subsequent fetches are user-triggered through filters/pagination.
  useEffect(() => { load({ page: 1 }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.max(1, Math.ceil(total / size));

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', p: { xs: 1.5, sm: 2.5, md: 3 }, bgcolor: '#F8FAFC' }}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 3,
          borderRadius: 2,
          borderColor: 'divider',
          bgcolor: '#FFFFFF'
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
              DATA REKRUTMEN
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Kelola kandidat yang masuk, unduh berkas, dan lakukan review cepat dari satu halaman.
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => load({ page: 1 })} disabled={loading}>
            MUAT ULANG
          </Button>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(3, minmax(0, 1fr))'
          },
          gap: 2,
          mb: 3
        }}
      >
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', bgcolor: '#ffffff' }}>
          <Typography variant="caption" color="text.secondary">TOTAL KANDIDAT</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>{total}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', bgcolor: '#ffffff' }}>
          <Typography variant="caption" color="text.secondary">DITAMPILKAN</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>{rows.length}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', bgcolor: '#ffffff' }}>
          <Typography variant="caption" color="text.secondary">HALAMAN</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>{page} / {totalPages}</Typography>
        </Paper>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 2,
          borderRadius: 2,
          borderColor: 'divider',
          bgcolor: '#FFFFFF'
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
          <TextField
            size="small"
            placeholder="CARI NAMA/EMAIL/TELEPON"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load({ page: 1, search }); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => load({ page: 1, search })}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ width: { xs: '100%', md: 420 } }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Tampilkan</InputLabel>
              <Select
                value={size}
                label="Tampilkan"
                onChange={handleSizeChange}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              onClick={() => {
                setSearch('');
                load({ page: 1, search: '', size });
              }}
            >
              RESET
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                <TableCell width={80} sx={{ fontWeight: 700 }}>Nomor</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>NAMA</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>EMAIL</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>TELEPON</TableCell>
                <TableCell width={300} align="center" sx={{ fontWeight: 700 }}>AKSI</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : rows.length ? rows.map((r, index) => (
                <TableRow key={r.id} hover>
                  <TableCell>{(page - 1) * size + index + 1}</TableCell>
                  <TableCell>{r.fullName}</TableCell>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>{r.phone}</TableCell>
                  <TableCell align="center">
                    <Button
                      component={Link}
                      to={`/admin/recruitment/${r.id}`}
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      sx={{ mr: 0.5, minWidth: '84px' }}
                    >
                      Detail
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={() => window.open(`${process.env.REACT_APP_API_URL || ''}/recruitment/applications/${r.id}/pdf`, '_blank')}
                      sx={{ mr: 0.5, minWidth: '72px' }}
                    >
                      PDF
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDelete(r.id)}
                      sx={{ minWidth: '82px' }}
                    >
                      Hapus
                    </Button>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    TIDAK ADA DATA
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => load({ page: p })}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminRecruitmentList;
